import React, { useState, useEffect } from 'react';
import { Sparkles, Check, Lock, Eye, EyeOff, RefreshCw, AlertTriangle } from '../../icons';
import { useAIConfig } from '../../hooks/useAIConfig';
import Button from '../ui/Button';
import { CardSection } from '../ui/Card';

const PROVIDER_LABELS = {
  gemini: 'Google Gemini',
  claude: 'Anthropic Claude',
  openai: 'OpenAI',
  perplexity: 'Perplexity',
};

const AISettings = ({ userId }) => {
  const ai = useAIConfig(userId);
  const [selProvider, setSelProvider] = useState('');
  const [selModel, setSelModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    setSelProvider(ai.provider);
    setSelModel(ai.model);
  }, [ai.provider, ai.model]);

  // Real providers only (hide the internal mock provider).
  const providers = (ai.providers || []).filter((p) => p.providerId !== 'mock');
  const modelsForProvider =
    providers.find((p) => p.providerId === selProvider)?.models || [];

  const handleProviderChange = (p) => {
    setSelProvider(p);
    const first = providers.find((x) => x.providerId === p)?.models?.[0]?.id || '';
    setSelModel(first);
  };

  const persistSelection = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await ai.setSelection(selProvider, selModel);
      setMessage({ type: 'success', text: 'Provider/model saved.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const handleSaveKey = async () => {
    if (!apiKey.trim()) return;
    setBusy(true);
    setMessage(null);
    try {
      await ai.saveKey(selProvider, apiKey.trim(), selModel);
      setApiKey('');
      setMessage({ type: 'success', text: `${PROVIDER_LABELS[selProvider] || selProvider} key saved and validated.` });
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const handleClearKey = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await ai.clearKey(ai.keyProvider || selProvider);
      setMessage({ type: 'success', text: 'Key removed.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  if (!ai.proxyConfigured) {
    return (
      <CardSection>
        <div className="flex items-start gap-3 text-sm text-primary">
          <AlertTriangle className="w-5 h-5 text-amber flex-shrink-0" />
          <span>AI proxy not configured. Set <code className="bg-page px-1 py-0.5 rounded text-[13px]">REACT_APP_AI_PROXY_URL</code> to enable AI features.</span>
        </div>
      </CardSection>
    );
  }

  if (ai.isLoading) {
    return (
      <CardSection>
        <div className="flex items-center gap-3 text-secondary text-sm">
          <RefreshCw className="w-5 h-5 animate-spin text-accent" /> Loading AI settings…
        </div>
      </CardSection>
    );
  }

  return (
    <CardSection
      title={
        <span className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-accent" /> AI provider & key
          {ai.lockedByAdmin && (
            <span className="ml-1 inline-flex items-center gap-1 text-[11px] font-semibold text-amber bg-amber-soft px-2 py-0.5 rounded-md">
              <Lock className="w-3 h-3" /> Managed by admin
            </span>
          )}
        </span>
      }
      className="space-y-4"
    >
      {ai.usingHouseKey && (
        <div className="p-3 rounded-xl bg-info-soft border border-info/30 text-primary text-sm">
          Your AI usage is provided by your administrator. No key needed.
        </div>
      )}

      {!ai.usingHouseKey && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="ai-provider">Provider</label>
              <select
                id="ai-provider"
                value={selProvider}
                disabled={ai.lockedByAdmin}
                onChange={(e) => handleProviderChange(e.target.value)}
                className="input disabled:opacity-50"
              >
                {providers.length === 0 && <option value={selProvider}>{selProvider}</option>}
                {providers.map((p) => (
                  <option key={p.providerId} value={p.providerId}>
                    {PROVIDER_LABELS[p.providerId] || p.providerId}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label" htmlFor="ai-model">Model</label>
              <select
                id="ai-model"
                value={selModel}
                disabled={ai.lockedByAdmin || !ai.allowModelSelection}
                onChange={(e) => setSelModel(e.target.value)}
                className="input disabled:opacity-50"
              >
                {modelsForProvider.length === 0 && <option value={selModel}>{selModel}</option>}
                {modelsForProvider.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
              {!ai.allowModelSelection && (
                <p className="text-xs text-muted mt-1">Model selection is disabled by your administrator.</p>
              )}
            </div>
          </div>

          {!ai.lockedByAdmin && (
            <Button
              variant="secondary"
              onClick={persistSelection}
              disabled={busy || (selProvider === ai.provider && selModel === ai.model)}
            >
              <Check className="w-4 h-4" /> Save provider/model
            </Button>
          )}

          {/* Key status / entry */}
          <div className="pt-4 border-t border-line">
            <label className="label" htmlFor="ai-key">
              API key for {PROVIDER_LABELS[selProvider] || selProvider}
            </label>

            {ai.hasUsableKey ? (
              <div className="flex items-center justify-between gap-3 bg-page border border-line rounded-xl px-3.5 py-2.5">
                <span className="text-sm text-accent flex items-center gap-2"><Check className="w-4 h-4" /> Key set ••••{ai.last4}</span>
                {!ai.lockedByAdmin && (
                  <button type="button" onClick={handleClearKey} disabled={busy} className="text-sm text-danger hover:underline disabled:opacity-50">
                    Remove
                  </button>
                )}
              </div>
            ) : (
              <>
                {ai.needsKey && (
                  <p className="text-xs text-amber mb-2">
                    No key set for this provider — automated scans are skipped until you add one.
                  </p>
                )}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      id="ai-key"
                      type={showKey ? 'text' : 'password'}
                      value={apiKey}
                      disabled={ai.lockedByAdmin}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="Paste your API key…"
                      className="input pr-11 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowKey((s) => !s)}
                      aria-label={showKey ? 'Hide key' : 'Show key'}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-lg text-secondary hover:text-primary"
                      tabIndex={-1}
                    >
                      {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button variant="primary" onClick={handleSaveKey} disabled={busy || ai.lockedByAdmin || !apiKey.trim()} loading={busy}>
                    {!busy && <Check className="w-4 h-4" />} Save key
                  </Button>
                </div>
                <p className="text-xs text-muted mt-2">
                  Your key is validated, then encrypted and stored server-side. It never stays in the browser.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {message && (
        <div
          role="status"
          className={`p-3 rounded-xl text-sm border ${
            message.type === 'success' ? 'bg-accent-soft text-accent border-accent/30' : 'bg-danger-soft text-danger border-danger/30'
          }`}
        >
          {message.text}
        </div>
      )}
    </CardSection>
  );
};

export default AISettings;
